import { ChangeEvent, JSX, useEffect, useState } from "react";

import { BreadcrumbTemplateEnum, Breadcrumbs, Page, PageHeader, PageInner, PageSection } from "@app/shared";
import GeocoderService, { GeoInfo, ResultAddress, SearchAddress } from "./geocoderService";
import { QESButton } from "@app/shared/qes-standards";
import { QesIcons } from "@app/shared/icons/qesIcons";

const computeEnabled = (searchAddress: SearchAddress) => (searchAddress && (addressEnabled(searchAddress) || cityEnabled(searchAddress) || stateEnabled(searchAddress) || zipEnabled(searchAddress)));
const addressEnabled = (searchAddress: SearchAddress) => (searchAddress && searchAddress.address.length > 0);
const cityEnabled = (searchAddress: SearchAddress) => (searchAddress && searchAddress.city.length > 0);
const stateEnabled = (searchAddress: SearchAddress) => (searchAddress && searchAddress.state.length > 1);
const zipEnabled = (searchAddress: SearchAddress) => (searchAddress && searchAddress.zip.length > 4);
const geocoderService = new GeocoderService();

export default function GeoLookupAddressView(): JSX.Element {
  const [hasResult, setHasResult] = useState<boolean>(false);
  const [resultAddress, setResultAddress] = useState<ResultAddress>({
    address: "",
    censusZipClass: "",
    city: "",
    countyClass: "",
    geoInfo: GeoInfo.unknown,
    latitude: 0,
    longitude: 0,
    popDensityZipClass: "",
    standardCBSA: "",
    standardCity: "",
    standardCounty: "",
    standardZip: "",
    state: "",
    success: false,
    zip: "",
  });
  const [address, setAddress] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [zip, setZip] = useState<string>("");
  const [searchEnabled, setSearchEnabled] = useState<boolean>(false);

  function handleSearchCriteriaChange(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    switch (e.target.name) {
      case "searchAddress":
        setAddress(value);
        break;
      case "searchCity":
        setCity(value);
        break;
      case "searchState":
        if (value.length > 0 && (!value.match("[a-zA-Z]") || value.length > 2)) {
          break;
        }
        setState(value.toUpperCase());
        break;
      case "searchZip":
        if (value.length > 0 && (!value.match("[0-9]") || value.length > 5)) {
          break;
        }
        setZip(value);
        break;
    }
  }

  useEffect(() => {
    const newEnabled = computeEnabled({ address, city, state, zip });
    if (newEnabled !== searchEnabled) {
      setSearchEnabled(newEnabled);
    }
  });

  async function handleSubmit() {
    // post a request to the geography service to get the data
    // unpack the response into state
    const result = await geocoderService.geocodeAddress({ address, city, state, zip });
    setHasResult(!!result);
    setResultAddress(result);
  }

  async function handleKeyUp(e: React.KeyboardEvent) {
    const value = e.key;
    if (value === "Enter" && computeEnabled({ address, city, state, zip })) {
      await handleSubmit();
    }
  }

  function displayGeoInfo(geoInfo: GeoInfo): string {
    switch (geoInfo) {
      case GeoInfo.addressExact:
        return "AddressExact";
      case GeoInfo.addressRelaxed:
        return "AddressRelaxed";
      case GeoInfo.addressRelaxedZipMismatch:
        return "AddressRelaxedZipMismatch";
      case GeoInfo.correctedZipNotInLicensedState:
        return "CorrectedZipNotInLicensedState";
      case GeoInfo.failOnInvalidZip:
        return "FailOnInvalidZip";
      case GeoInfo.failOnNotInCountyClassFilter:
        return "FailOnNotInCountyClassFilter";
      case GeoInfo.failOnNotInServiceAreaFilter:
        return "FailOnNotInServiceAreaFilter";
      case GeoInfo.failOnNotInZipClassFilter:
        return "FailOnNotInZipClassFilter";
      case GeoInfo.invalidZip:
        return "InvalidZip";
      case GeoInfo.notInLicensedState:
        return "NotInLicensedState";
      case GeoInfo.notYetProcessed:
        return "NotYetProcessed";
      case GeoInfo.zipDistributive:
        return "ZipDistributive";
      case GeoInfo.zipLegacy:
        return "ZipLegacy";
      case GeoInfo.zipPoint:
        return "ZipPoint";
      case GeoInfo.unknown:
      default:
        return "Unknown";
    }
  }

  return (
    <Page documentTitle="Lookup an Address">
      <Breadcrumbs
        template={BreadcrumbTemplateEnum.Tools}
        items={[
          {
            iconClass: "fa-map-marker",
            text: "Lookup an Address",
          },
        ]}
      />
      <PageInner>
        <PageHeader title="Lookup an Address" disableHR={false} iconName={QesIcons.FontAwesome.MapMarker} />
        <PageSection>
          <div className="row mt-3">
            <div className="col-12 col-sm-8">
              <div className="card card-expansion-item card-static">
                <div className="card-header">Search Criteria</div>
                <div className="card-body">
                  <div className="row mb-2">
                    <div className="col-sm-12">
                      <input className="form-control mb-2" placeholder="Address" value={address} onKeyUp={handleKeyUp} onChange={handleSearchCriteriaChange} name="searchAddress" />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-sm-4">
                      <input className="form-control mb-2" placeholder="City" value={city} onKeyUp={handleKeyUp} onChange={handleSearchCriteriaChange} name="searchCity" />
                    </div>
                    <div className="col-sm-4">
                      <input className="form-control mb-2" placeholder="State" value={state} onKeyUp={handleKeyUp} onChange={handleSearchCriteriaChange} name="searchState" />
                    </div>
                    <div className="col-sm-4">
                      <input className="form-control mb-2" placeholder="Zip" value={zip} onKeyUp={handleKeyUp} onChange={handleSearchCriteriaChange} name="searchZip" />
                    </div>
                  </div>
                  <div className="row justify-content-end">
                    <div className="col-sm-12">
                      <QESButton qesTestId="geo-lookup-address-view_search" qesType="primary" className="float-right" disabled={!searchEnabled} onClick={handleSubmit}>
                        <i className="fa fa-search" /> Search
                      </QESButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {hasResult && resultAddress.success &&
            <div className="row">
              <div className="col-12 col-sm-8">
                <div className="card card-expansion-item card-static">
                  <div className="card-header">Search Results</div>
                  <div className="card-body">
                    <div className="row">
                      {resultAddress.geoInfo !== GeoInfo.invalidZip ? (
                        <p className="lead text-success"><i className="fa fa-map-marker" />&nbsp;&nbsp;</p>
                      ) : (
                        <p className="lead text-danger"><i className="fa fa-map-marker" />&nbsp;&nbsp;</p>
                      )}

                      {(resultAddress.geoInfo === GeoInfo.addressExact || resultAddress.geoInfo === GeoInfo.addressRelaxed) && (
                        <p className="lead text-success">
                          {resultAddress.address}<br />
                          {resultAddress.city}, {resultAddress.state}  {resultAddress.zip}<br />
                        </p>
                      )}
                      {(resultAddress.geoInfo === GeoInfo.zipDistributive || resultAddress.geoInfo === GeoInfo.zipLegacy || resultAddress.geoInfo === GeoInfo.zipPoint) && (
                        <p className="lead text-success">
                          {resultAddress.zip}<br />
                        </p>
                      )}
                      {(resultAddress.geoInfo === GeoInfo.invalidZip) && (
                        <p className="lead text-danger">
                          {!!resultAddress.zip ? resultAddress.zip : "No zip code provided"}<br />
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-3 font-weight-bold mx-3 mb-3">Geocode&nbsp;Result:&nbsp;&nbsp;</div>
                    {resultAddress.geoInfo !== GeoInfo.invalidZip &&
                      <div className="col-sm-7 mx-3 mb-3 text-success"><i className="fa fa-check-circle" />&nbsp;{displayGeoInfo(resultAddress.geoInfo)}</div>
                    }
                    {resultAddress.geoInfo === GeoInfo.invalidZip &&
                      <div className="col-sm-7 mx-3 mb-3 text-danger"><i className="fa fa-times-circle" />&nbsp;{displayGeoInfo(resultAddress.geoInfo)}</div>
                    }
                  </div>
                  {(resultAddress.geoInfo !== GeoInfo.invalidZip) &&
                    <div>
                      <div className="row">
                        <div className="col-sm-3 font-weight-bold mx-3 mb-3">Standard&nbsp;Zip:&nbsp;&nbsp;</div>
                        <div className="col-sm-7 mx-3 mb-3">
                          {resultAddress.standardZip}<br />
                          <div className="text-warning">({resultAddress.popDensityZipClass} - <i>population density method</i>)</div>
                          <div className="text-warning">({resultAddress.censusZipClass} - <i>census method</i>)</div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-sm-3 font-weight-bold mx-3 mb-3">Standard&nbsp;City:&nbsp;&nbsp;</div>
                        <div className="col-sm-7 mx-3 mb-3">{resultAddress.standardCity}</div>
                      </div>
                      <div className="row">
                        <div className="col-sm-3 font-weight-bold mx-3 mb-3">Standard&nbsp;County:&nbsp;&nbsp;</div>
                        <div className="col-sm-7 mx-3 mb-3">
                          {resultAddress.standardCounty}<br />
                          <div className="text-warning">({resultAddress.countyClass})</div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-sm-3 font-weight-bold mx-3 mb-3">Standard&nbsp;CBSA:&nbsp;&nbsp;</div>
                        <div className="col-sm-7 mx-3 mb-3">{resultAddress.standardCBSA}</div>
                      </div>
                      <div className="row">
                        <div className="col-sm-3 font-weight-bold mx-3 mb-3">Geographic Coordinates:&nbsp;&nbsp;</div>
                        <div className="col-sm-7 mx-3 mb-3">{resultAddress.latitude}, {resultAddress.longitude}</div>
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          }
        </PageSection>
      </PageInner>
    </Page>
  );
}

export { GeoLookupAddressView };
